import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from '../lib/cn'

/**
 * Título da tela: Lexend 300 26px no conteúdo (não no cabeçalho), com subtítulo que diz o total
 * e o critério de ordenação ("331 cadastrados · 6 com pendência de assinatura").
 */
export interface PageTitleProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title: ReactNode
  subtitle?: ReactNode
  /** Linha de identificação sob o nome de uma ficha ("33 anos · autônoma · São Paulo"). */
  meta?: ReactNode
  /** Rótulo em caixa alta acima do título ("PACIENTE"). */
  eyebrow?: ReactNode
  /** Conteúdo à direita do título (filtros de período, etiqueta de status). */
  trailing?: ReactNode
}

export function PageTitle({
  title,
  subtitle,
  meta,
  eyebrow,
  trailing,
  className,
  ...props
}: PageTitleProps) {
  return (
    <div
      data-slot="page-title"
      className={cn('flex flex-wrap items-end justify-between gap-4', className)}
      {...props}
    >
      <div className="min-w-0">
        {eyebrow ? <div className="eyebrow mb-2">{eyebrow}</div> : null}
        <h1 className="font-display text-page text-ink font-light text-pretty">{title}</h1>
        {meta ? (
          <p className="font-body text-secondary mt-2 text-[13px] leading-[1.6] font-light">
            {meta}
          </p>
        ) : null}
        {subtitle ? (
          <p className="font-body text-secondary mt-2 text-[12px] leading-[1.6] font-light">
            {subtitle}
          </p>
        ) : null}
      </div>
      {trailing ? <div className="flex shrink-0 items-center gap-[10px]">{trailing}</div> : null}
    </div>
  )
}

/** Título de bloco dentro da página: Lexend 400 14px, com nota opcional dizendo onde o dado aparece depois. */
export function BlockTitle({
  title,
  note,
  trailing,
  className,
  ...props
}: Omit<HTMLAttributes<HTMLDivElement>, 'title'> & {
  title: ReactNode
  note?: ReactNode
  trailing?: ReactNode
}) {
  return (
    <div
      data-slot="block-title"
      className={cn('flex items-baseline justify-between gap-4', className)}
      {...props}
    >
      <div>
        <h2 className="font-display text-lead text-ink leading-[1.3] font-normal">{title}</h2>
        {note ? (
          <p className="font-body text-secondary mt-1 text-[12px] leading-[1.6] font-light">
            {note}
          </p>
        ) : null}
      </div>
      {trailing}
    </div>
  )
}
